import { motion } from 'framer-motion';
import { Eye, EyeOff, Hexagon, Loader2, Lock, Mail } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(true);
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const { resetPassword, updatePassword, supabase } = useAuth();
  const navigate = useNavigate();

  // Check if we're in local development
  const isLocalDev = 
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_SUPABASE_URL?.includes('localhost') ||
    process.env.REACT_APP_SUPABASE_URL?.includes('127.0.0.1');

  useEffect(() => {
    // Redirect to login in production (Google-only)
    if (!isLocalDev) {
      navigate('/login', { replace: true });
      return;
    }

    // Check if we're in password update mode (user clicked magic link)
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        setIsResetMode(false);
      }
    };

    checkSession();

    // Listen for auth state changes (e.g., when magic link is processed)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || session) {
        setIsResetMode(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase.auth, navigate, isLocalDev]);

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await resetPassword(email);
      setModal({
        isOpen: true,
        title: 'Reset Email Sent!',
        message:
          'Check your inbox and follow the link to reset your password. The link will expire in 1 hour.',
        type: 'success',
      });
    } catch (error: any) {
      setModal({
        isOpen: true,
        title: 'Failed to Send Email',
        message:
          error.message || 'Failed to send reset email. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (password !== confirmPassword) {
      setModal({
        isOpen: true,
        title: 'Password Mismatch',
        message: 'The passwords you entered do not match. Please try again.',
        type: 'error',
      });
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setModal({
        isOpen: true,
        title: 'Password Too Short',
        message: 'Password must be at least 6 characters long.',
        type: 'error',
      });
      setIsLoading(false);
      return;
    }

    try {
      await updatePassword(password);
      setModal({
        isOpen: true,
        title: 'Password Updated!',
        message:
          'Your password has been updated successfully. Redirecting to dashboard...',
        type: 'success',
      });
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error: any) {
      setModal({
        isOpen: true,
        title: 'Update Failed',
        message:
          error.message || 'Failed to update password. Please try again.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render in production
  if (!isLocalDev) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      <div className='min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-slate-100'>
        <div className='max-w-md w-full space-y-8'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className='text-center'
          >
            <div className='w-20 h-20 mx-auto mb-6'>
              <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center'>
                <Hexagon className='w-10 h-10 text-white' />
              </div>
            </div>
            <h2 className='text-3xl font-bold text-gray-900 mb-2'>
              {isResetMode ? 'Reset Password' : 'Set New Password'}
            </h2>
            <p className='text-gray-600'>
              {isResetMode
                ? "Enter your email address and we'll send you a link to reset your password"
                : 'Enter your new password below'}
            </p>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className='glass-effect rounded-2xl p-8'
          >
            <form
              className='space-y-6'
              onSubmit={isResetMode ? handleResetRequest : handlePasswordUpdate}
            >
              {isResetMode ? (
                <div>
                  <label
                    htmlFor='email'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Email Address
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <Mail className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      id='email'
                      name='email'
                      type='email'
                      autoComplete='email'
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className='block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent transition-all duration-200'
                      placeholder='Enter your email'
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label
                      htmlFor='password'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      New Password
                    </label>
                    <div className='relative'>
                      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                        <Lock className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        id='password'
                        name='password'
                        type={showPassword ? 'text' : 'password'}
                        autoComplete='new-password'
                        required
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className='block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent transition-all duration-200'
                        placeholder='Enter new password'
                      />
                      <button
                        type='button'
                        className='absolute inset-y-0 right-0 pr-3 flex items-center'
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        ) : (
                          <Eye className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor='confirmPassword'
                      className='block text-sm font-medium text-gray-700 mb-2'
                    >
                      Confirm New Password
                    </label>
                    <div className='relative'>
                      <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                        <Lock className='h-5 w-5 text-gray-400' />
                      </div>
                      <input
                        id='confirmPassword'
                        name='confirmPassword'
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete='new-password'
                        required
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        className='block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent transition-all duration-200'
                        placeholder='Confirm new password'
                      />
                      <button
                        type='button'
                        className='absolute inset-y-0 right-0 pr-3 flex items-center'
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        ) : (
                          <Eye className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <button
                type='submit'
                disabled={isLoading}
                className='w-full hexagon-button flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isLoading ? (
                  <>
                    <Loader2 className='w-5 h-5 animate-spin' />
                    <span>
                      {isResetMode
                        ? 'Sending Reset Email...'
                        : 'Updating Password...'}
                    </span>
                  </>
                ) : (
                  <span>
                    {isResetMode ? 'Send Reset Email' : 'Update Password'}
                  </span>
                )}
              </button>
            </form>

            <div className='mt-6 text-center'>
              <Link
                to='/login'
                className='text-hexagon hover:text-hexagon-dark text-sm font-medium transition-colors duration-200'
              >
                ← Back to Sign In
              </Link>
            </div>
          </motion.div>

          {/* Back to Home */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className='text-center'
          >
            <Link
              to='/'
              className='text-gray-600 hover:text-hexagon text-sm transition-colors duration-200'
            >
              ← Back to Home
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
